import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import PostcodeFilter from "../components/PostcodeFilter";
import SearchAutocomplete from "../components/SearchAutocomplete";
import { useCart } from "../lib/cart";

const CATEGORIES = ["All", "Dry Foods", "Fresh Produce", "Protein", "Spices", "Oils", "Flours", "Kits", "Sauces"];

export default function ProductList() {
  const { postcode } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const search = searchParams.get("search") || "";
  const category = searchParams.get("category") || "All";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (category !== "All") params.set("category", category);
      if (postcode) params.set("postcode", postcode);
      const { data } = await api.get(`/products?${params.toString()}`);
      setProducts(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[products] load failed:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [category, postcode, search]);

  const handleSearchSubmit = (q) => {
    const p = new URLSearchParams(searchParams);
    if (q) p.set("search", q);
    else p.delete("search");
    setSearchParams(p);
  };

  const setCategory = (cat) => {
    const p = new URLSearchParams(searchParams);
    if (cat === "All") p.delete("category");
    else p.set("category", cat);
    setSearchParams(p);
  };

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4 md:px-8 lg:px-12" data-testid="products-page">
      <div className="mb-8">
        <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Catalogue</p>
        <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-4">Every African pantry essential</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <SearchAutocomplete initial={search} onSubmit={handleSearchSubmit} />
        <div className="md:w-64"><PostcodeFilter /></div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-8 pb-2">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`category-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setCategory(c)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-all ${c === category ? "bg-[#1A1A18] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5C54] hover:border-[#D1CEC5]"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="aspect-[3/4] bg-white rounded-2xl animate-pulse border border-[#E5E2D9]" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="no-products-message">
          <p className="text-sm text-[#5C5C54]">No products match your filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      )}
    </div>
  );
}
